import { styled } from '@mui/material/styles';
import { StatusVariant } from './Status';
import { useState } from 'react';
import { Grid, useTheme } from '@mui/material';
import { ConfirmationCentered } from '../ConfirmationDialog/ConfirmationDialog';
import { Trans, useTranslation } from 'react-i18next';
import { deletePlan } from '../../../features/advisorConsole';
import { useAppDispatch, useAppSelector, useIsMultiPortfolioTenant } from '../../../common/hooks';
import { updateGlobalLoaderState } from '../../../features/global/globalSlice';
import { FEATURE_NAMES, STATUSVARIANTS } from '../../../common/constants';
import { TableWithCustomRender } from '../TableWithCustomRender';
import { InfoTooltip } from '../../../components/molecules/InfoTooltip';
import { Status } from './Status';
import CustomRow from './Row/CustomAdminRow';
import {
    formatCompactCurrency,
    PdfDownloadToastState,
    getClientJourneyFlowType,
    checkFFEnabled,
    isTenantAdmin
} from '../../../utils';
import { CustomSubRow } from './SubRow/CustomAdminSubRow';
import { ActionRequiredIndicator } from './ActionRequiredIndicator';
import { ActionRequiredGoal } from '../../../features/advisorConsole/types';
import { GoalTypeEnum } from '../../../common/types';
import dayjs from 'dayjs';
import { getMaskedStatus } from '../../../features/advisorConsole/utils';

const TableScroll = styled('div')(() => ({
    overflowX: 'auto'
}));

export type SubRowProps = {
    goal: GoalTypeEnum;
    portfolioValue: number;
    probabilityofSuccess?: number;
    equityFixedIncome?: string;
    id: string;
    goalPriority: string;
    goalType?: string;
};

export type AdminTableItem = {
    id: string;
    status: StatusVariant;
    advisorName?: string;
    probabilityOfSuccess?: number;
    clientGoalName: string;
    portfolioApproach?: string;
    riskGoalPriority: string;
    portfolioValue?: number;
    numberofGoals: number;
    equityFixedIncome?: string;
    subItems?: SubRowProps[];
    lastUpdated?: string;
    actionRequiredGoalCount?: number;
    actionRequiredGoals?: ActionRequiredGoal[];
};

type Props = {
    items: AdminTableItem[];
    advisorConsoleRevampFF?: boolean;
    loading?: boolean;
    // eslint-disable-next-line no-unused-vars
    setPdfToast?: (value: PdfDownloadToastState) => void;
    // eslint-disable-next-line no-unused-vars
    setConsoleErrorBanner?: (planId: string, hasError: boolean) => void;
    pdfErrorPlanIds?: string[];
};
type ColumnAlign = 'left' | 'center' | 'right' | 'inherit' | 'justify';

export const ConsoleTable = ({
    items = [],
    loading = false,
    setPdfToast,
    setConsoleErrorBanner,
    pdfErrorPlanIds
}: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const tenant = useAppSelector((state) => state?.auth?.user?.tenant);
    const user = useAppSelector((state) => state?.auth?.user);
    const dispatch = useAppDispatch();
    const [planToDelete, setPlanToDelete] = useState<string | undefined>(undefined);
    const dateFormat = useAppSelector((state) => state.global)?.globalConfig?.dateFormat || 'MM/DD/YYYY';
    const isFlow2 = getClientJourneyFlowType(tenant) === 'flow2';
    const isUserTenantAdmin = isTenantAdmin(user);
    // AEX-3570: gate the "N Goals need attention" indicator to flow2 + feature flag.
    const notificationsFFEnabled = checkFFEnabled(FEATURE_NAMES['AEX-3570_NOTIFICATIONS_IN_GETPROPOSALS']);
    const isAmountFormatCompactFF = checkFFEnabled(FEATURE_NAMES['AMOUNT_FORMAT_COMPACT_FE_AEX-3635']);
    const isMultiPortfolioTenant = useIsMultiPortfolioTenant();

    const onConfirmDelete = async () => {
        try {
            if (planToDelete) {
                dispatch(updateGlobalLoaderState(true));
                await dispatch(deletePlan({ tenant, planId: planToDelete }));
                dispatch(updateGlobalLoaderState(false));
                setPlanToDelete(undefined);
            }
        } catch (e) {
            console.error(`Error deleting plan with id ${planToDelete}`);
        }
    };

    const rowHeaderNew: {
        label: string;
        key: string;
        align: ColumnAlign;
        width: string;
        // eslint-disable-next-line no-unused-vars
        render?: (value: any, row: any) => React.ReactNode;
        disabled?: boolean;
        subKey?: string;
        admin?: boolean;
    }[] = [
        ...(isUserTenantAdmin
            ? [
                  {
                      label: t('ADVISOR_CONSOLE_ADV_NAME'),
                      key: 'advisorName',
                      align: 'left' as ColumnAlign,
                      width: '10%',
                      admin: true
                  }
              ]
            : []),
        {
            label: t('COL_PLAN_CLIENT_NAME'),
            key: 'clientGoalName',
            align: 'left',
            width: '13%',
            admin: isUserTenantAdmin,
            render: (value, row) => {
                const actionRequiredCount = row?.actionRequiredGoalCount ?? 0;
                const showActionRequired = notificationsFFEnabled && isFlow2 && actionRequiredCount > 0;
                return (
                    <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', gap: 8 }}>
                        <span style={{ textDecorationLine: 'underline' }}>{value ? t(value.toUpperCase()) : ''}</span>
                        <span
                            style={{
                                color: theme.palette.semantic.text.muted,
                                fontFamily: '"TT Commons Pro"',
                                fontSize: 12,
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: 'var(--Font-Line-height-leading-3, 17px)' /* 141.667% */
                            }}
                        >
                            {row?.participantId ?? ''}
                        </span>
                        {showActionRequired && <ActionRequiredIndicator count={actionRequiredCount} />}
                    </div>
                );
            }
        },
        {
            label: t('COL_HOLDINGS'),
            key: 'portfolioValue',
            align: 'right',
            width: '8%',
            ...((isAmountFormatCompactFF && {
                render: (value: number | string) => (value || value === 0 ? formatCompactCurrency(value) : '')
            }) ||
                {})
        },
        ...(!isFlow2 && !isMultiPortfolioTenant
            ? [
                  {
                      label: t('CLIENT_PROFILE_RISK_TOLERANCE_HELPER'),
                      key: 'riskGoalPriority',
                      align: 'left' as ColumnAlign,
                      width: '10%',
                      render: (value: any) => {
                          return <div style={{ display: 'flex' }}>{value ? t(value.toUpperCase()) : ''}</div>;
                      }
                  }
              ]
            : []),
        {
            label: t('LABEL_LAST_UPDATED'),
            key: 'lastUpdated',
            align: 'left',
            width: '12%',
            render: (value: string) => (value ? dayjs(value, 'MM/DD/YYYY').format(dateFormat) : '')
        },
        {
            label: t('NEW_ADVISOR_CONSOLE_STATUS'),
            key: 'status',
            align: 'left',
            width: '13%',
            render: (value: StatusVariant) => {
                return (
                    <div style={{ display: 'flex' }}>
                        <InfoTooltip
                            message={
                                value === 'complete' ? t('STATUS_PILL_COMPLETE_TEXT') : t('STATUS_PILL_INCOMPLETE_TEXT')
                            }
                            placement="bottom-start"
                            style={{}}
                        >
                            <Status variant={getMaskedStatus(value, isFlow2)} />
                        </InfoTooltip>
                    </div>
                );
            }
        },
        { label: t('NEW_ADVISOR_CONSOLE_ACTIONS'), key: 'actions', align: 'left', width: '8%' }
    ];

    const getRowHeaders = () => rowHeaderNew;

    return (
        <>
            {planToDelete && (
                <Grid
                    width="100%"
                    height="100%"
                    position="absolute"
                    container
                    justifyContent="center"
                    alignItems="center"
                >
                    <ConfirmationCentered
                        cancelButtonLabel={t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT')}
                        confirmButtonLabel={t(
                            checkFFEnabled(FEATURE_NAMES['NEW_TOOLTIP_FTDS_AEX-3428'])
                                ? 'DELETE_CONFIRMATION_MODAL_DELETE_TEXT'
                                : 'DELETE_TEXT'
                        )}
                        title={t('DELETE_PLAN_TITLE_ADMIN_TABLE_REVAMP')}
                        content={
                            <Trans
                                i18nKey={
                                    checkFFEnabled(FEATURE_NAMES['NEW_TOOLTIP_FTDS_AEX-3428'])
                                        ? 'DELETE_PLAN_CONTENT_FTDS'
                                        : 'DELETE_PLAN_CONTENT_ADMIN_TABLE_REVAMP'
                                }
                                components={{ strong: <strong /> }}
                                values={{ clientName: items.find((item) => item.id === planToDelete)?.clientGoalName }}
                            />
                        }
                        onCancellation={() => setPlanToDelete(undefined)}
                        onCloseIconClick={() => setPlanToDelete(undefined)}
                        onConfirmation={() => onConfirmDelete()}
                        open
                    />
                </Grid>
            )}
            <TableScroll>
                <TableWithCustomRender
                    headers={getRowHeaders()}
                    rowHeight="64px"
                    dataSource={items}
                    loading={loading}
                    customRowComp={(headers, row, toggleRow, isExpanded, rowKey) => (
                        <CustomRow
                            rowData={row}
                            columns={headers}
                            toggleRow={toggleRow}
                            isExpanded={isExpanded}
                            rowKey={rowKey}
                            setPdfToast={setPdfToast}
                            setConsoleErrorBanner={setConsoleErrorBanner}
                            pdfErrorPlanIds={pdfErrorPlanIds}
                            enablePDFGenerate={row.status === STATUSVARIANTS.COMPLETE}
                            enablePlanDetails={row.status === STATUSVARIANTS.COMPLETE}
                            enableReallocate={row.status === STATUSVARIANTS.COMPLETE}
                            onDeleteClick={(planId: string) => setPlanToDelete(planId)}
                            showExpandButton={false}
                        />
                    )}
                    customSubRowComp={(headers, row) => <CustomSubRow rowData={row} columns={headers} />}
                />
            </TableScroll>
        </>
    );
};
